import type { ConnectorRecord, ConnectorTypeKey } from '@brunel/shared';

export const SYSTEM_CONNECTOR_TYPES = [
  'supabase_project',
  'github_repository',
  'cloudflare_pages',
] as const;

export type SystemConnectorType = (typeof SYSTEM_CONNECTOR_TYPES)[number];
export type SystemMonitorStatus = 'Healthy' | 'Attention' | 'Failing' | 'Unavailable' | 'Unknown';

export interface SystemMonitorFact {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly link: string | null;
  /** Why a figure is unknown, or the limit of a figure that was measured. */
  readonly detail: string | null;
}

export interface SystemMonitorCard {
  readonly typeKey: SystemConnectorType;
  readonly label: string;
  readonly status: SystemMonitorStatus;
  readonly summary: string;
  readonly connectorId: string | null;
  readonly observedAt: string | null;
  readonly lastSuccessfulAt: string | null;
  readonly source: string;
  readonly facts: readonly SystemMonitorFact[];
}

interface Descriptor {
  readonly typeKey: SystemConnectorType;
  readonly label: string;
  readonly facts: readonly FactDescriptor[];
}

interface FactDescriptor {
  readonly id: string;
  readonly label: string;
  readonly keys?: readonly string[];
  readonly noteKeys?: readonly string[];
  readonly kind?: 'text' | 'boolean' | 'number' | 'percent' | 'sha' | 'url' | 'supabase_dashboard';
  readonly pair?: readonly [readonly string[], readonly string[]];
}

/** The database readings the Command Center workspace shows for Supabase. */
export const DATABASE_HEALTH_FACTS = [
  { id: 'connections', label: 'DB connections' },
  { id: 'db_health', label: 'Database health' },
  { id: 'failed_queries', label: 'Failed queries' },
  { id: 'rpc_calls', label: 'RPC calls' },
  { id: 'migration_state', label: 'Migration state' },
  { id: 'storage_recorded', label: 'Storage recorded' },
] as const;

/** System evidence older than one normal node-capacity freshness window is not current. */
export const SYSTEM_EVIDENCE_STALE_AFTER_MS = 300_000;

const DESCRIPTORS: readonly Descriptor[] = [
  {
    typeKey: 'supabase_project',
    label: 'Supabase',
    facts: [
      { id: 'compute_size', label: 'Compute size', keys: ['computeSize', 'computeTier', 'instanceSize'] },
      { id: 'cpu', label: 'Database CPU', keys: ['cpuUsagePercent', 'databaseCpuPercent'], kind: 'percent' },
      { id: 'memory', label: 'Database memory', keys: ['memoryUsagePercent', 'databaseMemoryPercent'], kind: 'percent' },
      { id: 'disk_io_budget', label: 'Disk IO budget used', keys: ['diskIoBudgetUsedPercent'], kind: 'percent' },
      { id: 'project', label: 'Project', keys: ['projectRef', 'project', 'projectName'] },
      { id: 'service_health', label: 'Service connectivity', keys: ['serviceHealth'] },
      { id: 'project_health', label: 'Project health', keys: ['projectHealth', 'projectStatus', 'status'] },
      { id: 'database', label: 'Database reachable', keys: ['databaseReachable', 'dbReachable', 'dbHealthy'], kind: 'boolean' },
      { id: 'db_health', label: 'Database health', keys: ['databaseHealth'], noteKeys: ['databaseHealthNote'] },
      { id: 'connections', label: 'DB connections', pair: [['databaseConnectionsUsed', 'dbConnectionsUsed'], ['databaseConnectionsMax', 'dbConnectionsMax']] },
      { id: 'failed_queries', label: 'Failed queries', keys: ['failedQueryCount'], kind: 'number', noteKeys: ['failedQueriesNote'] },
      { id: 'rpc_calls', label: 'RPC calls', keys: ['rpcCallCount'], kind: 'number', noteKeys: ['rpcCallsNote'] },
      { id: 'storage', label: 'Storage', keys: ['storageStatus', 'storageHealth', 'storageHealthy'], kind: 'boolean' },
      { id: 'storage_recorded', label: 'Storage recorded', keys: ['storageSummary'], noteKeys: ['storageNote'] },
      { id: 'auth', label: 'Auth', keys: ['authStatus', 'authHealth', 'authHealthy'], kind: 'boolean' },
      { id: 'functions_count', label: 'Edge Functions', keys: ['edgeFunctionsCount', 'functionsCount'], kind: 'number' },
      { id: 'functions_status', label: 'Functions status', keys: ['edgeFunctionsStatus', 'functionsStatus', 'functionsHealthy'], kind: 'boolean' },
      { id: 'migration_expected', label: 'Expected migration', keys: ['expectedMigrationTip', 'repoMigrationTip'] },
      { id: 'migration_applied', label: 'Applied migration', keys: ['appliedMigrationTip', 'hostedMigrationTip'] },
      { id: 'migration_status', label: 'Migration status', keys: ['migrationStatus', 'migrationSyncStatus'] },
      { id: 'migration_state', label: 'Migration state', keys: ['migrationState'], noteKeys: ['migrationStateNote'] },
      { id: 'resource_report', label: 'Live resource report', keys: ['projectRef'], kind: 'supabase_dashboard' },
    ],
  },
  {
    typeKey: 'github_repository',
    label: 'GitHub',
    facts: [
      { id: 'repository', label: 'Repository', keys: ['repository', 'repositoryName', 'repo'] },
      { id: 'reachable', label: 'Reachable', keys: ['reachable', 'repositoryReachable'], kind: 'boolean' },
      { id: 'main_sha', label: 'Current main', keys: ['mainSha', 'currentMainSha'], kind: 'sha' },
      { id: 'open_prs', label: 'Open pull requests', keys: ['openPullRequestCount', 'openPrCount'], kind: 'number' },
      { id: 'open_issues', label: 'Open issues', keys: ['openIssueCount'], kind: 'number' },
      { id: 'main_checks', label: 'Main checks', keys: ['mainChecksStatus', 'checksStatus'] },
      { id: 'workflow', label: 'Latest workflow', keys: ['latestWorkflowStatus', 'latestActionsStatus'] },
      { id: 'release', label: 'Latest release', keys: ['latestRelease', 'latestReleaseTag'] },
      { id: 'deployment_commit', label: 'Latest deployment commit', keys: ['latestDeploymentCommit', 'deployedCommit'], kind: 'sha' },
      { id: 'failure', label: 'Latest failure', keys: ['latestFailureSummary', 'failureSummary'] },
      { id: 'failure_link', label: 'Failure evidence', keys: ['latestFailureUrl', 'failureUrl'], kind: 'url' },
    ],
  },
  {
    typeKey: 'cloudflare_pages',
    label: 'Cloudflare',
    facts: [
      { id: 'project', label: 'Pages project', keys: ['projectName', 'pagesProject'] },
      { id: 'hostname', label: 'Hostname', keys: ['hostname', 'productionHostname'] },
      { id: 'reachable', label: 'Reachable', keys: ['reachable'], kind: 'boolean' },
      { id: 'deployment', label: 'Latest deployment', keys: ['latestDeploymentStatus', 'deploymentStatus'] },
      { id: 'deployed_commit', label: 'Deployed commit', keys: ['deployedCommit', 'deploymentCommit'], kind: 'sha' },
      { id: 'deployed_at', label: 'Deployed at', keys: ['deployedAt', 'deploymentAt'] },
      { id: 'failure', label: 'Latest failed build', keys: ['latestFailureSummary', 'failedBuildSummary'] },
      { id: 'failure_link', label: 'Build evidence', keys: ['latestFailureUrl', 'failedBuildUrl'], kind: 'url' },
    ],
  },
];

function firstValue(detail: Readonly<Record<string, unknown>>, keys: readonly string[]): unknown {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(detail, key)) return detail[key];
  }
  return undefined;
}

function safeText(value: unknown, max = 200): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > max) return null;
  return trimmed;
}

function safeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function safeUrl(value: unknown): string | null {
  const text = safeText(value, 500);
  if (!text) return null;
  try {
    const parsed = new URL(text);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function factValue(descriptor: FactDescriptor, detail: Readonly<Record<string, unknown>>): SystemMonitorFact {
  const note = descriptor.noteKeys ? safeText(firstValue(detail, descriptor.noteKeys), 240) : null;
  const done = (value: string, link: string | null): SystemMonitorFact => ({
    id: descriptor.id,
    label: descriptor.label,
    value,
    link,
    detail: note,
  });
  if (descriptor.pair) {
    const used = safeNumber(firstValue(detail, descriptor.pair[0]));
    const max = safeNumber(firstValue(detail, descriptor.pair[1]));
    return done(used !== null && max !== null && max > 0 ? `${used}/${max}` : 'Unknown', null);
  }
  const raw = firstValue(detail, descriptor.keys ?? []);
  if (descriptor.kind === 'supabase_dashboard') {
    const projectRef = safeText(raw, 64);
    const link = projectRef && /^[a-z0-9]{8,40}$/.test(projectRef)
      ? `https://supabase.com/dashboard/project/${projectRef}/reports/database`
      : null;
    return done(link ? 'Open live metrics' : 'Unknown', link);
  }
  if (descriptor.kind === 'boolean') {
    const value = typeof raw === 'boolean' ? (raw ? 'Healthy' : 'Failing') : safeText(raw);
    return done(value ?? 'Unknown', null);
  }
  if (descriptor.kind === 'number') {
    const value = safeNumber(raw);
    return done(value === null ? 'Unknown' : String(value), null);
  }
  if (descriptor.kind === 'percent') {
    const value = safeNumber(raw);
    return done(value !== null && value <= 100 ? `${Math.round(value * 10) / 10}%` : 'Unknown', null);
  }
  if (descriptor.kind === 'url') {
    const link = safeUrl(raw);
    return done(link ? 'Open evidence' : 'Unknown', link);
  }
  const text = safeText(raw);
  const value = descriptor.kind === 'sha' && text ? text.slice(0, 12) : text;
  return done(value ?? 'Unknown', null);
}

/** Database health lines for the Command Center workspace. A missing card stays Unknown. */
export function databaseHealthFacts(card: SystemMonitorCard | null): readonly SystemMonitorFact[] {
  const byId = new Map((card?.facts ?? []).map((fact) => [fact.id, fact]));
  return DATABASE_HEALTH_FACTS.map((spec) => byId.get(spec.id) ?? {
    id: spec.id,
    label: spec.label,
    value: 'Unknown',
    link: null,
    detail: 'No Supabase connector evidence.',
  });
}

function normalized(value: unknown): string | null {
  const text = safeText(value, 80);
  return text?.toLowerCase().replaceAll('_', ' ').replaceAll('-', ' ') ?? null;
}

function evidenceTime(connector: ConnectorRecord | null): string | null {
  if (!connector) return null;
  return safeText(connector.healthDetail['evidenceTimestamp'], 80)
    ?? safeText(connector.healthDetail['observedAt'], 80)
    ?? safeText(connector.healthDetail['serviceObservedAt'], 80)
    ?? connector.lastHealthAt;
}

function evidenceIsCurrent(connector: ConnectorRecord, nowMs: number): boolean {
  const timestamp = evidenceTime(connector);
  if (!timestamp) return false;
  const observedMs = Date.parse(timestamp);
  if (Number.isNaN(observedMs)) return false;
  const ageMs = nowMs - observedMs;
  return ageMs >= -60_000 && ageMs <= SYSTEM_EVIDENCE_STALE_AFTER_MS;
}

function capacityPercent(detail: Readonly<Record<string, unknown>>, keys: readonly string[]): number | null {
  const value = safeNumber(firstValue(detail, keys));
  return value !== null && value <= 100 ? value : null;
}

function supabaseCapacityStatus(detail: Readonly<Record<string, unknown>>): SystemMonitorStatus {
  const cpu = capacityPercent(detail, ['cpuUsagePercent', 'databaseCpuPercent']);
  const memory = capacityPercent(detail, ['memoryUsagePercent', 'databaseMemoryPercent']);
  const diskIo = capacityPercent(detail, ['diskIoBudgetUsedPercent']);
  if (cpu === null || memory === null || diskIo === null) return 'Unknown';
  if (cpu >= 95 || memory >= 95 || diskIo >= 95) return 'Failing';
  if (cpu >= 80 || memory >= 80 || diskIo >= 75) return 'Attention';
  return 'Healthy';
}

function statusFor(connector: ConnectorRecord | null, typeKey: SystemConnectorType, nowMs: number): SystemMonitorStatus {
  if (!connector) return 'Unknown';
  if (connector.connectionMode === 'unavailable' || connector.healthStatus === 'unavailable' || connector.authState === 'signed_out') {
    return 'Unavailable';
  }
  if (connector.status !== 'active') return 'Unknown';
  if (!evidenceIsCurrent(connector, nowMs)) return 'Unknown';

  const detail = connector.healthDetail;
  const failureValues = [
    detail['reachable'] === false,
    detail['repositoryReachable'] === false,
    detail['databaseReachable'] === false,
    detail['dbReachable'] === false,
    detail['dbHealthy'] === false,
    detail['storageHealthy'] === false,
    detail['authHealthy'] === false,
    detail['functionsHealthy'] === false,
    detail['edgeFunctionsHealthy'] === false,
  ];
  const states = [
    detail['projectHealth'], detail['projectStatus'], detail['status'], detail['mainChecksStatus'],
    detail['checksStatus'], detail['latestWorkflowStatus'], detail['latestActionsStatus'],
    detail['latestDeploymentStatus'], detail['deploymentStatus'],
  ].map(normalized).filter((value): value is string => value !== null);
  if (states.some((value) => ['unavailable', 'unreachable'].includes(value))) return 'Unavailable';
  if (failureValues.some(Boolean) || states.some((value) => ['failed', 'failing', 'failure', 'error', 'offline', 'unhealthy', 'cancelled'].includes(value))) {
    return 'Failing';
  }

  const migration = normalized(detail['migrationStatus'] ?? detail['migrationSyncStatus']);
  const migrationSeverity = normalized(detail['migrationSeverity']);
  if (migration === 'behind' && ['critical', 'failing', 'error'].includes(migrationSeverity ?? '')) return 'Failing';
  if (
    migration === 'behind'
    || states.some((value) => ['attention', 'degraded', 'warning', 'in progress', 'pending'].includes(value))
    || connector.healthStatus === 'degraded'
  ) return 'Attention';

  if (typeKey === 'supabase_project') return supabaseCapacityStatus(detail);
  if (connector.healthStatus === 'healthy') return 'Healthy';
  return 'Unknown';
}

function latestConnector(connectors: readonly ConnectorRecord[], typeKey: ConnectorTypeKey): ConnectorRecord | null {
  return connectors
    .filter((row) => row.connectorType === typeKey && row.status !== 'revoked')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null;
}

function sourceFor(connector: ConnectorRecord | null): string {
  if (!connector) return 'No supported connector evidence';
  return safeText(connector.healthDetail['source'], 120) ?? 'Canonical connector registry';
}

function observedAt(connector: ConnectorRecord | null): string | null {
  return evidenceTime(connector);
}

function lastSuccessfulAt(connector: ConnectorRecord | null): string | null {
  if (!connector) return null;
  return safeText(connector.healthDetail['lastSuccessfulCheckAt'], 80)
    ?? (connector.healthStatus === 'healthy' ? connector.lastHealthAt : null)
    ?? connector.lastSyncAt;
}

function summaryFor(
  status: SystemMonitorStatus,
  connector: ConnectorRecord | null,
  typeKey: SystemConnectorType,
  nowMs: number,
): string {
  if (connector && !evidenceIsCurrent(connector, nowMs)) {
    return 'The last service check is stale. Current health and capacity are unknown.';
  }
  const stated = safeText(connector?.healthDetail['summary'], 160)
    ?? safeText(connector?.healthDetail['reason'], 160);
  if (stated) return stated;
  if (typeKey === 'supabase_project' && status === 'Unknown' && connector) {
    return 'Service connectivity is available, but CPU, memory and Disk IO are not currently reported to Brunel.';
  }
  if (status === 'Healthy') return 'Fresh timestamped evidence reports healthy.';
  if (status === 'Attention') return 'Current evidence needs attention.';
  if (status === 'Failing') return 'Current evidence reports a failure.';
  if (status === 'Unavailable') return 'A supported secure monitoring source is unavailable.';
  return 'No current supported evidence is available.';
}

export function buildSystemsMonitorCards(
  connectors: readonly ConnectorRecord[],
  nowMs = Date.now(),
): readonly SystemMonitorCard[] {
  return DESCRIPTORS.map((descriptor) => {
    const connector = latestConnector(connectors, descriptor.typeKey);
    const status = statusFor(connector, descriptor.typeKey, nowMs);
    return {
      typeKey: descriptor.typeKey,
      label: descriptor.label,
      status,
      summary: summaryFor(status, connector, descriptor.typeKey, nowMs),
      connectorId: connector?.id ?? null,
      observedAt: observedAt(connector),
      lastSuccessfulAt: lastSuccessfulAt(connector),
      source: sourceFor(connector),
      facts: descriptor.facts.map((fact) => factValue(fact, connector?.healthDetail ?? {})),
    };
  });
}
